import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, MessageCircle } from 'lucide-react';
import { WHATSAPP_LINK, HERO_STATS } from '../constants';

const DEMO_WHATSAPP_LINK = `${WHATSAPP_LINK}?text=${encodeURIComponent('Hola, quiero contaros qué software uso y ver qué puede automatizar Ancora en mi negocio.')}`;

export const HeroSection: React.FC = () => {
  return (
    <>
    <section id="hero" className="relative bg-slate-950 text-white pt-16 pb-16 overflow-hidden min-h-[600px] flex items-center dark-noise">
      {/* Video Background */}
      <div className="absolute inset-0 z-0">
        <video
          autoPlay
          muted
          loop
          playsInline
          poster="https://images.pexels.com/photos/8386440/pexels-photo-8386440.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1"
          className="w-full h-full object-cover opacity-25"
        >
          <source src="./hero-video.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/80 via-slate-950/50 to-slate-950" />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950/80 via-transparent to-slate-950/80" />
        <div className="absolute top-20 left-[10%] w-80 h-80 bg-primary rounded-full filter blur-[120px] opacity-15 animate-blob" />
        <div className="absolute top-10 right-[15%] w-72 h-72 bg-cyan-600 rounded-full filter blur-[100px] opacity-10 animate-blob animation-delay-2000" />
        <div className="absolute bottom-0 left-[30%] w-96 h-96 bg-secondary rounded-full filter blur-[130px] opacity-12 animate-blob animation-delay-4000" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.025)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.025)_1px,transparent_1px)] bg-[size:60px_60px] [mask-image:radial-gradient(ellipse_80%_70%_at_50%_30%,#000_60%,transparent_100%)] pointer-events-none" />
      </div>

      <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden" style={{ bottom: '40%' }}>
        <div className="particle p1" />
        <div className="particle p2" />
        <div className="particle p3" />
        <div className="particle p4" />
        <div className="particle p5" />
        <div className="particle p6" />
      </div>

      <div className="container mx-auto px-4 max-w-6xl relative z-10">
        <div className="text-center max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex justify-center mb-5"
          >
            <span
              className="inline-flex items-center gap-2 py-1.5 px-4 rounded-full text-sm font-bold tracking-widest uppercase"
              style={{
                background: 'rgba(60,179,113,0.12)',
                border: '1px solid rgba(60,179,113,0.35)',
                boxShadow: '0 0 20px rgba(60,179,113,0.2), inset 0 1px 0 rgba(255,255,255,0.08)',
                color: '#7ee8a2',
              }}
            >
              <span className="w-2 h-2 rounded-full bg-secondary animate-pulse"></span>
              IA conectada a tu operativa real
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="font-black mb-5 leading-[1.05] tracking-tight font-display"
            style={{ fontSize: 'clamp(2.4rem, 6vw, 4.5rem)', letterSpacing: '-0.02em' }}
          >
            IA integrada para{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #3CB371 0%, #7ee8a2 40%, #22d3ee 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                display: 'inline-block',
                filter: 'drop-shadow(0 0 30px rgba(60,179,113,0.4))',
              }}
            >
              atender, vender y crecer
            </span>{' '}
            con tu negocio
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-lg md:text-xl text-slate-300 mb-6 max-w-2xl mx-auto leading-relaxed font-light text-balance"
          >
            Conectamos WhatsApp, voz, Instagram, Google Business y tu software actual para atender mejor, vender más y no perder oportunidades.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="flex flex-wrap justify-center gap-4 mb-6"
          >
            <span className="inline-flex items-center gap-2 text-sm text-slate-300">
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Se integra con tu software actual
            </span>
            <span className="inline-flex items-center gap-2 text-sm text-slate-300">
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Ventas y marketing por módulos
            </span>
            <span className="inline-flex items-center gap-2 text-sm text-slate-300">
              <svg className="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
              WhatsApp Base desde 9,99€/mes
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-6"
          >
            <a
              href={DEMO_WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 text-slate-900 font-extrabold text-lg px-10 py-4 rounded-full transition-all duration-300 hover:-translate-y-1"
              style={{
                background: 'linear-gradient(135deg, #3CB371, #2d8653)',
                boxShadow: '0 0 30px rgba(60,179,113,0.35), 0 4px 20px rgba(0,0,0,0.2)',
              }}
            >
              <MessageCircle size={20} />
              Dinos qué software usas
            </a>
            <a
              href="#integraciones"
              className="inline-flex items-center justify-center gap-2 font-semibold text-lg px-10 py-4 rounded-full transition-all duration-300 hover:bg-white/10 hover:-translate-y-1"
              style={{
                background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(255,255,255,0.15)',
                backdropFilter: 'blur(10px)',
                color: 'white',
              }}
            >
              Ver integraciones
              <ArrowRight size={16} />
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.42 }}
            className="text-sm text-slate-400 max-w-2xl mx-auto"
          >
            No tienes que migrar de plataforma: añadimos una capa de IA sobre las herramientas que ya usas.
          </motion.p>
        </div>
      </div>
    </section>

    <section className="bg-slate-950 text-white px-4 pb-16">
      <div className="container mx-auto max-w-6xl text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto mb-10"
        >
          <div className="flex justify-center mb-3">
            <span className="text-yellow-400 text-xl tracking-widest">★★★★★</span>
          </div>
          <p className="text-slate-300 text-base md:text-lg italic mb-4 leading-relaxed">
            "Antes tenía una carpeta llena de consentimientos en papel. Ahora el cliente firma desde su móvil y si viene una inspección estoy cubierto."
          </p>
          <div className="flex items-center justify-center gap-2 text-sm">
            <span className="text-white font-medium">Raúl</span>
            <span className="text-slate-500">·</span>
            <span className="text-slate-400">Seven Sisters Tattoo · Madrid</span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 max-w-3xl mx-auto mb-10"
        >
          {HERO_STATS.map((stat, idx) => (
            <div
              key={idx}
              className="rounded-2xl p-4 md:p-6 text-center"
              style={{
                background: 'rgba(255,255,255,0.05)',
                backdropFilter: 'blur(12px)',
                border: '1px solid rgba(255,255,255,0.1)',
              }}
            >
              <div className="text-2xl mb-2">{stat.icon}</div>
              <div className="text-2xl md:text-3xl font-black text-white">{stat.value}</div>
              <div className="text-xs md:text-sm text-slate-400 mt-1 uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        <p className="text-xs text-slate-600">
          Tecnología basada en WhatsApp Business API, voz IA, Meta, Google y automatizaciones conectadas a tu operativa
        </p>
      </div>
    </section>
    </>
  );
};
